import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  CheckCircle,
  Info,
  Pause,
} from "lucide-react";
import { cn } from "../../lib/utils";

const statusPillVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full h-8 px-3 py-1 text-xs font-bold uppercase tracking-wider",
  {
    variants: {
      variant: {
        warning:
          "bg-orange-50 text-orange-400 dark:bg-orange-800 dark:text-orange-300",
        error: "bg-red-50 text-red-400 dark:bg-red-800 dark:text-red-300",
        waiting: "bg-blue-50 text-blue-400 dark:bg-blue-800 dark:text-blue-300",
        success:
          "bg-green-50 text-green-400 dark:bg-green-800 dark:text-green-300",
        neutral: "bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-300",
        paused:
          "bg-yellow-50 text-yellow-600 dark:bg-yellow-800 dark:text-yellow-300",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  }
);

const iconMap = {
  warning: AlertTriangle,
  error: AlertCircle,
  waiting: Clock,
  success: CheckCircle,
  neutral: Info,
  paused: Pause,
} as const;

export interface StatusPillProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusPillVariants> {
  variant: "warning" | "error" | "waiting" | "success" | "neutral" | "paused";
  children: React.ReactNode;
}

function StatusPill({
  className,
  variant = "neutral",
  children,
  ...props
}: StatusPillProps) {
  // Use switch to avoid object injection warning
  let IconComponent;
  switch (variant) {
    case "warning":
      IconComponent = iconMap.warning;
      break;
    case "error":
      IconComponent = iconMap.error;
      break;
    case "waiting":
      IconComponent = iconMap.waiting;
      break;
    case "success":
      IconComponent = iconMap.success;
      break;
    case "paused":
      IconComponent = iconMap.paused;
      break;
    case "neutral":
    default:
      IconComponent = iconMap.neutral;
      break;
  }

  return (
    <div className={cn(statusPillVariants({ variant }), className)} {...props}>
      <IconComponent size={16} />
      <span className="text-xs font-bold uppercase tracking-wider leading-none">
        {children}
      </span>
    </div>
  );
}

export default StatusPill;
